import { useState, useEffect } from "react";
import { Tag, Sparkles, RefreshCw, Gift, Ticket, AlertCircle, CheckCircle } from "lucide-react";
import { Button } from "./ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const CouponPriceDisplay = ({ 
  originalPrice, 
  currency = "₪",
  product, // 'kit' or 'product' or 'pack5'
  language = 'he',
  onCouponApplied,
  onWhatsAppClick
}) => {
  const [coupon, setCoupon] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAnimation, setShowAnimation] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  
  // Manual coupon input state
  const [manualCode, setManualCode] = useState("");
  const [isApplying, setIsApplying] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    fetchUserCoupon();
  }, []);

  const fetchUserCoupon = async () => {
    try {
      const storedToken = localStorage.getItem('mart_session_token');
      if (!storedToken) {
        setIsLoading(false);
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/coupons/user-coupon`, {
        credentials: "include",
        headers: {
          'Authorization': `Bearer ${storedToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.has_coupon && !data.used) {
          setCoupon(data);
          setShowAnimation(true);
          setTimeout(() => setShowAnimation(false), 2000);
          if (onCouponApplied) {
            onCouponApplied(data);
          }
        }
      }
    } catch (error) {
      console.log("Error fetching coupon:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyManualCoupon = async () => {
    if (!manualCode.trim()) {
      setErrorMessage(language === 'he' ? 'נא להזין קוד קופון' : 'Please enter a coupon code');
      return;
    }

    const code = manualCode.toUpperCase().trim();
    
    // Check if GOOGLE20 is entered without being logged in
    if (code === 'GOOGLE20' && !isLoggedIn) {
      setErrorMessage(language === 'he' 
        ? '⚠️ קוד GOOGLE20 זמין רק למשתמשים מחוברים עם גוגל. התחברו תחילה!' 
        : '⚠️ GOOGLE20 code is only available for Google users. Please sign in first!'
      );
      return;
    }

    setIsApplying(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const storedToken = localStorage.getItem('mart_session_token');
      
      // If not logged in and trying to use GOOGLE20, show error
      if (!storedToken && code === 'GOOGLE20') {
        setErrorMessage(language === 'he' 
          ? '⚠️ קוד GOOGLE20 זמין רק למשתמשים מחוברים עם גוגל. התחברו תחילה!' 
          : '⚠️ GOOGLE20 code is only available for Google users. Please sign in first!'
        );
        setIsApplying(false);
        return;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/coupons/apply`, {
        method: 'POST',
        credentials: "include",
        headers: {
          'Authorization': `Bearer ${storedToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          code: code,
          product 
        })
      });

      if (response.ok) {
        const data = await response.json();
        setCoupon({
          has_coupon: true,
          code: data.code,
          discount: data.discount,
          description: data.description,
          can_reset: data.can_reset,
          used: false
        });
        setShowAnimation(true);
        setTimeout(() => setShowAnimation(false), 2000);
        setSuccessMessage(language === 'he' 
          ? `קופון ${data.code} הופעל בהצלחה! ${data.discount}% הנחה` 
          : `Coupon ${data.code} applied! ${data.discount}% off`
        );
        setManualCode("");
        if (onCouponApplied) {
          onCouponApplied(data);
        }
      } else {
        const errorData = await response.json();
        setErrorMessage(language === 'he' 
          ? `קוד קופון לא תקף. ${coupon?.code === 'GOOGLE20' ? '' : 'זכרו: קופון GOOGLE20 פעיל אוטומטית למשתמשי גוגל!'}`
          : `Invalid coupon code. ${coupon?.code === 'GOOGLE20' ? '' : 'Remember: GOOGLE20 coupon is automatically active for Google users!'}`
        );
      }
    } catch (error) {
      console.log("Error applying coupon:", error);
      setErrorMessage(language === 'he' 
        ? 'שגיאה בהפעלת הקופון. נסו שוב.' 
        : 'Error applying coupon. Please try again.'
      );
    } finally {
      setIsApplying(false);
    }
  };

  const handleResetCoupon = async () => {
    if (!coupon?.can_reset) return;
    
    setIsResetting(true);
    try {
      const storedToken = localStorage.getItem('mart_session_token');
      const response = await fetch(`${BACKEND_URL}/api/coupons/reset`, {
        method: 'POST',
        credentials: "include",
        headers: {
          'Authorization': `Bearer ${storedToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ product })
      });

      if (response.ok) {
        await fetchUserCoupon();
      }
    } catch (error) {
      console.log("Error resetting coupon:", error);
    } finally {
      setIsResetting(false);
    }
  };

  const discountedPrice = coupon 
    ? Math.round(originalPrice * (1 - coupon.discount / 100))
    : originalPrice;

  const content = {
    he: {
      discount: "הנחה",
      freeDelivery: "חינם!",
      resetCoupon: "איפוס קופון",
      resetDescription: "לחצו לאיפוס הקופון ולתת למישהו אחר",
      couponApplied: "קופון הופעל!",
      yourDiscount: "ההנחה שלך",
      specialOffer: "מבצע מיוחד למתחברים עם גוגל",
      originalPrice: "מחיר מקורי",
      haveCoupon: "יש לכם קוד קופון?",
      enterCode: "הזינו קוד קופון",
      apply: "הפעל",
      applying: "מפעיל..."
    },
    en: {
      discount: "discount",
      freeDelivery: "Free!",
      resetCoupon: "Reset Coupon",
      resetDescription: "Click to reset coupon and give to someone else",
      couponApplied: "Coupon Applied!",
      yourDiscount: "Your discount",
      specialOffer: "Special offer for Google users",
      originalPrice: "Original price",
      haveCoupon: "Have a coupon code?",
      enterCode: "Enter coupon code",
      apply: "Apply",
      applying: "Applying..."
    }
  };

  const t = content[language] || content.he;

  // Check if user is logged in
  const isLoggedIn = !!localStorage.getItem('mart_session_token');

  if (isLoading) {
    return (
      <div className="flex items-baseline gap-2 justify-center animate-pulse">
        <div className="h-12 w-24 bg-gray-200 rounded"></div>
      </div>
    );
  }

  // No coupon - show manual input
  if (!coupon) {
    return (
      <div className="space-y-4">
        <div className="flex items-baseline gap-2 justify-center">
          <span className="font-serif text-5xl font-medium text-[#2C3329] dark:text-white">
            {originalPrice} {currency}
          </span>
        </div>
        
        {/* Google Users Note */}
        <div className="flex items-start gap-3 p-3 bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 rounded-xl border border-blue-100 dark:border-blue-800/30">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-green-500 flex items-center justify-center">
            <svg className="h-4 w-4 text-white" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-[#2C3329] dark:text-white">
              {language === 'he' ? 'קופון למשתמשי גוגל' : 'Google Users Coupon'}
            </p>
            <p className="text-xs text-[#6B7266] dark:text-[#9A9790] mt-0.5">
              {language === 'he' 
                ? 'התחברו עם גוגל והקלידו GOOGLE20 לקבלת 20% הנחה!' 
                : 'Sign in with Google and enter GOOGLE20 for 20% off!'
              }
            </p>
          </div>
        </div>
        
        {/* Manual Coupon Input */}
        <div className="bg-[#F5F2EB] dark:bg-[#1a231a] rounded-xl p-4 border border-[#E8E4DC] dark:border-[#2a3a2a]">
          <div className="flex items-center gap-2 mb-3 text-sm text-[#4A5D43] dark:text-[#9ACD8A]">
            <Ticket className="h-4 w-4" />
            <span className="font-medium">{t.haveCoupon}</span>
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={manualCode}
              onChange={(e) => {
                setManualCode(e.target.value.toUpperCase());
                setErrorMessage("");
                setSuccessMessage("");
              }}
              placeholder={t.enterCode}
              className="flex-1 px-3 py-2 rounded-lg border border-[#E8E4DC] dark:border-[#2a3a2a] bg-white dark:bg-[#121812] text-[#2C3329] dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#4A5D43]/50"
              onKeyDown={(e) => e.key === 'Enter' && handleApplyManualCoupon()}
            />
            <Button
              onClick={handleApplyManualCoupon}
              disabled={isApplying || !manualCode.trim()}
              className="bg-[#4A5D43] hover:bg-[#3A4A35] text-white px-4"
            >
              {isApplying ? t.applying : t.apply}
            </Button>
          </div>
          
          {errorMessage && (
            <div className="mt-2 flex items-start gap-2 text-red-600 dark:text-red-400 text-sm">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}
          
          {successMessage && (
            <div className="mt-2 flex items-center gap-2 text-green-600 dark:text-green-400 text-sm">
              <CheckCircle className="h-4 w-4" />
              <span>{successMessage}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // 100% discount - FREE
  if (coupon.discount === 100) {
    return (
      <div className="space-y-4">
        {/* Coupon Badge */}
        <div className={`inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full shadow-lg ${showAnimation ? 'animate-bounce' : ''}`}>
          <Gift className="h-4 w-4 text-white" />
          <span className="text-white font-bold text-sm">
            {coupon.code} - 100% {t.discount}! 🎉
          </span>
        </div>

        {/* Price Display */}
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-3">
            <span className="text-2xl text-gray-400 line-through decoration-red-500 decoration-2">
              {originalPrice} {currency}
            </span>
            
            <svg className="w-6 h-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
            
            <span className={`font-serif text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-500 to-emerald-500 ${showAnimation ? 'animate-pulse' : ''}`}>
              {t.freeDelivery}
            </span>
          </div>

          {showAnimation && (
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              {[...Array(12)].map((_, i) => (
                <Sparkles 
                  key={i}
                  className="absolute text-yellow-400 animate-ping"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 0.5}s`,
                    width: '16px',
                    height: '16px'
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Reset Button for 100% coupons */}
        {coupon.can_reset && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResetCoupon}
              disabled={isResetting}
              className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 dark:text-purple-400 dark:hover:bg-purple-900/20"
            >
              <RefreshCw className={`h-4 w-4 ${language === 'he' ? 'ml-2' : 'mr-2'} ${isResetting ? 'animate-spin' : ''}`} />
              {t.resetCoupon}
            </Button>
            <p className="text-xs text-gray-500 mt-1">{t.resetDescription}</p>
          </div>
        )}

        {/* Manual Coupon Input - for changing to another code */}
        <div className="bg-[#F5F2EB] dark:bg-[#1a231a] rounded-xl p-4 border border-[#E8E4DC] dark:border-[#2a3a2a]">
          <div className="flex items-center gap-2 mb-3 text-sm text-[#4A5D43] dark:text-[#9ACD8A]">
            <Ticket className="h-4 w-4" />
            <span className="font-medium">{language === 'he' ? 'להחליף לקוד אחר?' : 'Switch to another code?'}</span>
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={manualCode}
              onChange={(e) => {
                setManualCode(e.target.value.toUpperCase());
                setErrorMessage("");
                setSuccessMessage("");
              }}
              placeholder={t.enterCode}
              className="flex-1 px-3 py-2 rounded-lg border border-[#E8E4DC] dark:border-[#2a3a2a] bg-white dark:bg-[#121812] text-[#2C3329] dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#4A5D43]/50"
              onKeyDown={(e) => e.key === 'Enter' && handleApplyManualCoupon()}
            />
            <Button
              onClick={handleApplyManualCoupon}
              disabled={isApplying || !manualCode.trim()}
              size="sm"
              className="bg-[#4A5D43] hover:bg-[#3A4A35] text-white"
            >
              {isApplying ? '...' : t.apply}
            </Button>
          </div>
          {errorMessage && (
            <div className="mt-2 text-red-600 dark:text-red-400 text-xs">{errorMessage}</div>
          )}
        </div>
      </div>
    );
  }

  // Regular discount (10-20%)
  return (
    <div className="space-y-3">
      {/* Coupon Badge */}
      <div className={`inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full shadow-lg ${showAnimation ? 'animate-bounce' : ''}`}>
        <Tag className="h-4 w-4 text-white" />
        <span className="text-white font-bold text-sm">
          {coupon.discount}% {t.discount}
        </span>
        <Sparkles className="h-4 w-4 text-yellow-300" />
      </div>

      {/* Special Offer Note */}
      {coupon.code === 'GOOGLE20' && (
        <p className="text-xs text-green-600 dark:text-green-400 font-medium">
          ✨ {t.specialOffer}
        </p>
      )}

      {/* Price Display */}
      <div className="flex flex-col items-center gap-2">
        <div className="flex items-center gap-3 flex-wrap justify-center">
          <div className="flex flex-col items-center">
            <span className="text-xs text-gray-400">{t.originalPrice}</span>
            <span className="text-2xl text-gray-400 line-through decoration-red-500 decoration-2">
              {originalPrice} {currency}
            </span>
          </div>
          
          <svg className={`w-6 h-6 text-green-500 ${showAnimation ? 'animate-pulse' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
          
          <div className="flex flex-col items-center">
            <span className="text-xs text-green-500 font-medium">{t.yourDiscount}</span>
            <span className={`font-serif text-5xl font-medium text-[#2C3329] dark:text-white ${showAnimation ? 'animate-pulse scale-110' : ''} transition-transform`}>
              {discountedPrice} {currency}
            </span>
          </div>
        </div>

        <div className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 dark:bg-green-900/30 rounded-full">
          <span className="text-green-700 dark:text-green-300 text-sm font-medium">
            {language === 'he' ? 'חסכת' : 'You save'} {originalPrice - discountedPrice} {currency}!
          </span>
        </div>
      </div>

      {/* Manual Coupon Input - for upgrading to better code */}
      <div className="bg-[#F5F2EB] dark:bg-[#1a231a] rounded-xl p-4 border border-[#E8E4DC] dark:border-[#2a3a2a]">
        <div className="flex items-center gap-2 mb-3 text-sm text-[#4A5D43] dark:text-[#9ACD8A]">
          <Ticket className="h-4 w-4" />
          <span className="font-medium">{language === 'he' ? 'יש לכם קוד קופון נוסף?' : 'Have another coupon code?'}</span>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={manualCode}
            onChange={(e) => {
              setManualCode(e.target.value.toUpperCase());
              setErrorMessage("");
              setSuccessMessage("");
            }}
            placeholder={t.enterCode}
            className="flex-1 px-3 py-2 rounded-lg border border-[#E8E4DC] dark:border-[#2a3a2a] bg-white dark:bg-[#121812] text-[#2C3329] dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#4A5D43]/50"
            onKeyDown={(e) => e.key === 'Enter' && handleApplyManualCoupon()}
          />
          <Button
            onClick={handleApplyManualCoupon}
            disabled={isApplying || !manualCode.trim()}
            size="sm"
            className="bg-[#4A5D43] hover:bg-[#3A4A35] text-white"
          >
            {isApplying ? '...' : t.apply}
          </Button>
        </div>
        {errorMessage && (
          <div className="mt-2 flex items-start gap-2 text-red-600 dark:text-red-400 text-xs">
            <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}
        {successMessage && (
          <div className="mt-2 flex items-center gap-2 text-green-600 dark:text-green-400 text-xs">
            <CheckCircle className="h-3 w-3" />
            <span>{successMessage}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default CouponPriceDisplay;
